import {
    useCallback,
    useState,
} from 'react';
import {
    Link,
    useNavigate,
} from 'react-router-dom';
import {
    ArrowLeft,
    ClipboardList,
    type LucideIcon,
    ShoppingBag,
    Store,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
    cn,
    setLocalStorage,
} from '@/lib/utils';

interface OnboardingStep {
    icon: LucideIcon;
    title: string;
    description: string;
    iconBgClass: string;
    iconColorClass: string;
}

const STEPS: OnboardingStep[] = [
    {
        icon: ShoppingBag,
        title: 'Become a Buyer',
        description: 'Discover the best products from all available suppliers.',
        iconBgClass: 'bg-primary/10',
        iconColorClass: 'text-primary',
    },
    {
        icon: Store,
        title: 'Become a Seller',
        description: 'Upload various products for customers around the platform with best range.',
        iconBgClass: 'bg-accent/15',
        iconColorClass: 'text-accent',
    },
    {
        icon: ClipboardList,
        title: 'Create your own S&C List',
        description: 'Add & Remove suppliers/customers in your list for easy access.',
        iconBgClass: 'bg-primary/10',
        iconColorClass: 'text-primary',
    },
];

const STORAGE_KEY = 'chanco-onboarding-completed';

export default function Onboarding() {
    const [currentStep, setCurrentStep] = useState(0);
    const navigate = useNavigate();

    const completeOnboarding = useCallback(() => {
        setLocalStorage(STORAGE_KEY, 'true');
        navigate('/login');
    }, [navigate]);

    const handleNext = useCallback(() => {
        if (currentStep < STEPS.length - 1) {
            setCurrentStep((prev) => prev + 1);
        } else {
            completeOnboarding();
        }
    }, [currentStep, completeOnboarding]);

    const handleBack = useCallback(() => {
        if (currentStep > 0) {
            setCurrentStep((prev) => prev - 1);
        } else {
            navigate('/');
        }
    }, [currentStep, navigate]);

    const handleSkip = useCallback(() => {
        completeOnboarding();
    }, [completeOnboarding]);

    const step = STEPS[currentStep];
    const isLastStep = currentStep === STEPS.length - 1;
    const IconComponent = step.icon;

    return (
        <div className="min-h-screen bg-background flex flex-col">
            {/* Top bar with back button and logo */}
            <div className="flex items-center justify-between p-6">
                <button
                    type="button"
                    onClick={handleBack}
                    className="flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                    <ArrowLeft className="h-4 w-4" />
                    Back
                </button>
                <Link to="/" className="flex items-center hover:opacity-80 transition-opacity">
                    <img
                        src="/chanco-horizontal.png"
                        alt="Chanco"
                        className="h-7 w-auto"
                    />
                </Link>
                {/* Spacer to center the logo */}
                <div className="w-14" />
            </div>

            {/* Main content */}
            <div className="flex-1 flex flex-col items-center justify-center px-6 pb-8">
                {/* Illustration circle */}
                <div className={cn(
                    'w-40 h-40 rounded-full flex items-center justify-center mb-10 shadow-sm',
                    step.iconBgClass,
                )}
                >
                    <IconComponent className={cn('w-20 h-20 stroke-[1.5]', step.iconColorClass)} />
                </div>

                {/* Title */}
                <h2 className="text-2xl font-bold text-foreground text-center mb-3">
                    {step.title}
                </h2>

                {/* Description */}
                <p className="text-sm text-muted-foreground text-center max-w-xs leading-relaxed">
                    {step.description}
                </p>

                {/* Dot indicators */}
                <div className="flex gap-2 mt-10">
                    {STEPS.map((_, index) => (
                        <button
                            // eslint-disable-next-line react/no-array-index-key
                            key={index}
                            type="button"
                            onClick={() => setCurrentStep(index)}
                            className={cn(
                                'h-2 rounded-full transition-all duration-300',
                                index === currentStep
                                    ? 'w-8 bg-primary'
                                    : 'w-2 bg-muted-foreground/30',
                            )}
                            aria-label={`Go to step ${index + 1}`}
                        />
                    ))}
                </div>
            </div>

            {/* Bottom navigation */}
            <div className="p-6 flex items-center justify-between">
                <button
                    type="button"
                    onClick={handleSkip}
                    className="text-sm text-muted-foreground hover:text-foreground transition-colors"
                >
                    Skip
                </button>
                <Button
                    size="lg"
                    className="px-8 rounded-lg"
                    onClick={handleNext}
                >
                    {isLastStep ? 'Get Started' : 'Next'}
                </Button>
            </div>
        </div>
    );
}
